import type { SettingTerm } from './types'

/**
 * Lowercases, strips accents, reads `&` as "and", and collapses punctuation
 * to single spaces, so "Roles & Permissions", "roles and permissions" and
 * "roles-permissions" all compare equal.
 */
export function normalizeSearchText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
}

export interface SearchableSettingsItem {
  readonly label: string
  readonly keywords?: readonly string[]
  /** Settings rendered inside this item, from the generated search index. */
  readonly settings?: readonly SettingTerm[]
}

export interface SettingsSearchMatch {
  /** Lower is better; see {@link matchSettingsItem} for the tiers. */
  readonly score: number
  /** Set when the item matched through one of its settings rather than its own name. */
  readonly matchedSetting?: SettingTerm
}

const words = (text: string) => text.split(' ')

/**
 * Scores how well a settings item matches a query, or returns null. Tiers,
 * best first:
 *
 * 0 label prefix · 1 label word prefix · 2 label substring ·
 * 3 keyword prefix · 4 keyword substring ·
 * 5 setting title prefix · 6 setting title word prefix · 7 setting title substring ·
 * 8 every query word in the label and keywords ·
 * 9 every query word across the label, keywords and one setting title ·
 * 10 a setting's description contains the query (or all its words)
 *
 * Single-character queries only match label prefixes to keep noise down.
 * When several settings match, the best-scoring one is reported.
 */
export function matchSettingsItem(
  item: SearchableSettingsItem,
  query: string,
): SettingsSearchMatch | null {
  const q = normalizeSearchText(query)
  if (!q) return null

  const label = normalizeSearchText(item.label)
  if (label.startsWith(q)) return { score: 0 }
  if (q.length < 2) return null
  if (words(label).some(word => word.startsWith(q))) return { score: 1 }
  if (label.includes(q)) return { score: 2 }

  const keywords = (item.keywords ?? []).map(normalizeSearchText)
  if (keywords.some(keyword => keyword.startsWith(q))) return { score: 3 }
  if (keywords.some(keyword => keyword.includes(q))) return { score: 4 }

  const settings = (item.settings ?? []).map(setting => ({
    setting,
    title: normalizeSearchText(setting.title),
    description: setting.description
      ? normalizeSearchText(setting.description)
      : '',
  }))

  const state: { best: SettingsSearchMatch | null } = { best: null }
  const consider = (score: number, setting: SettingTerm) => {
    if (!state.best || score < state.best.score) {
      state.best = { score, matchedSetting: setting }
    }
  }

  for (const entry of settings) {
    if (entry.title.startsWith(q)) consider(5, entry.setting)
    else if (words(entry.title).some(word => word.startsWith(q))) {
      consider(6, entry.setting)
    } else if (entry.title.includes(q)) consider(7, entry.setting)
  }
  if (state.best) return state.best

  const tokens = words(q)
  const hasAllTokens = (haystack: string) =>
    tokens.every(token => haystack.includes(token))
  const itemHaystack = [label, ...keywords].join(' ')

  if (tokens.length > 1 && hasAllTokens(itemHaystack)) return { score: 8 }

  for (const entry of settings) {
    if (tokens.length > 1 && hasAllTokens(`${itemHaystack} ${entry.title}`)) {
      consider(9, entry.setting)
    }
    if (
      q.length >= 3 &&
      entry.description &&
      (entry.description.includes(q) ||
        (tokens.length > 1 &&
          hasAllTokens(`${entry.title} ${entry.description}`)))
    ) {
      consider(10, entry.setting)
    }
  }
  return state.best
}
